'use client'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useRef } from 'react'

export interface IModalProps {
    isOpen: boolean
    onClose: () => void
    children: React.ReactNode
    headerText?: string
    className?: string
    /** Mostra o botão de fechar (padrão: sim). */
    closeBtn?: boolean
}

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Tab no último elemento volta ao primeiro, e Shift+Tab no primeiro (ou no próprio dialog) vai ao último. */
function trapFocus(event: KeyboardEvent, dialog: HTMLElement) {
    const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
    const active = document.activeElement
    if (focusables.length === 0) {
        event.preventDefault()
        dialog.focus()
        return
    }
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const outside = !dialog.contains(active)
    if (event.shiftKey && (active === first || active === dialog || outside)) {
        event.preventDefault()
        last.focus()
    } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault()
        first.focus()
    }
}

/**
 * Modal da loja (padrão do orbit): fecha pelo botão, pelo Escape e pelo clique fora, e trava a rolagem da página
 * enquanto aberto. Ao abrir, o foco vai para o dialog e fica preso nele (Tab e Shift+Tab circulam dentro); ao fechar,
 * volta para quem estava com o foco antes. Renderizado no lugar (sem portal) para manter a mesma sobreposição de antes.
 */
const Modal: React.FC<IModalProps> = ({ isOpen, onClose, children, headerText, className = '', closeBtn = true }) => {
    const modalRef = useRef<HTMLDivElement>(null)
    // O onClose costuma ser recriado a cada render: guardado em ref, não reinicia os efeitos (nem rouba o foco).
    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (!isOpen) return
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
        modalRef.current?.focus()

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onCloseRef.current()
            else if (event.key === 'Tab' && modalRef.current) trapFocus(event, modalRef.current)
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) onCloseRef.current()
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.body.style.overflow = ''
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handleClickOutside)
            if (previousFocus?.isConnected) previousFocus.focus()
        }
    }, [isOpen])

    if (!isOpen) return null

    return (
        <div className="z-999 fixed inset-0 flex items-center justify-center">
            <div className="fixed inset-0 bg-gray-600/50 backdrop-blur-xs"></div>
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-label={headerText}
                tabIndex={-1}
                className={`relative outline-hidden p-4 bg-white rounded-md shadow-lg max-w-[80vw] max-md:w-[90vw] ${className}`}
            >
                <div className="flex justify-between items-center mb-4">
                    {headerText ? (
                        <h2 className=" text-2xl font-light font-default text-destaques">{headerText}</h2>
                    ) : null}
                    {closeBtn ? (
                        <button
                            type="button"
                            aria-label="Fechar"
                            onClick={onClose}
                            className={`${headerText ? '' : 'absolute top-2 right-8'} hover:text-red-500`}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    ) : null}
                </div>
                <div className="overflow-y-auto max-h-[80vh]">{children}</div>
            </div>
        </div>
    )
}

export default Modal
